import * as React from 'react';
import * as H from 'history';
import { observer, inject } from 'mobx-react';
import { observable, computed, action } from 'mobx';
import * as classNames from 'classnames';
import { Redirect } from 'react-router-dom';
import { IAppOnlyStore } from 'stores/AllStores';
import { LoadingSpinner } from 'components/indicator';

// Styles
const s = require('./LoginForm.css');
const crmLogo = require('assets/crm-logo.png');

// Enumerables
enum LoginStatus {
  Default,
  LoggingIn,
  Success,
  AllFieldsRequired,
  WrongPasswordOrUsername,
}

export enum HeadlineType {
  Normal,
  Important,
}

// Props
interface ILoginFormProps extends IAppOnlyStore {
  headline?: string;
  headlineType?: HeadlineType;
  showBack?: boolean;
  readonly location: H.Location;
  readonly history: H.History;
}

// Component
@inject('store')
@observer
export class LoginForm extends React.Component<ILoginFormProps, {}> {
  static get defaultProps(): Partial<ILoginFormProps> {
    return {
      headlineType: HeadlineType.Normal,
      showBack: false,
    };
  }

  constructor(props) {
    super(props);
  }

  @observable private status: LoginStatus = LoginStatus.Default;
  @observable private username: string = '';
  @observable private password: string = '';

  @computed private get isError(): boolean {
    return (
      this.status === LoginStatus.AllFieldsRequired ||
      this.status === LoginStatus.WrongPasswordOrUsername
    );
  }
  @computed private get label(): string {
    let label = 'Sign in';

    if (this.isError) {
      label =
        this.status === LoginStatus.WrongPasswordOrUsername
          ? 'Wrong password or username, try again'
          : 'All fields are required, try again';
    } else if (this.status === LoginStatus.LoggingIn) {
      label = 'Signing in...';
    } else if (this.status === LoginStatus.Success) {
      label = 'Signed in';
    }

    return label;
  }

  @computed private get redirectPath(): string {
    // Prepare from route
    let from = '/';
    if (
      typeof this.props.location !== 'undefined' &&
      typeof this.props.location.state !== 'undefined'
    ) {
      if (
        this.props.location.state !== null &&
        typeof this.props.location.state.from !== 'undefined'
      ) {
        from = this.props.location.state.from;
      }
    }

    // Return default or from route
    return from === '/' && LoginStatus.Success ? `/sales` : from;
  }

  public render() {
    return (
      <div className={s.block}>
        <div className={s.top}>
          <img className={s.logo} src={crmLogo} />

          {this.props.showBack && (
            <button
              tabIndex={4}
              className={s.goBack}
              onClick={this.handleGoBack}
            >
              Go back
            </button>
          )}
        </div>

        {this.props.headline &&
          this.props.store.utilities.isConnectedToBackEnd && (
            <h1
              className={classNames({
                [s.headline]: true,
                [s.headlineImportant]:
                  this.props.headlineType === HeadlineType.Important,
              })}
            >
              {this.props.headline}
            </h1>
          )}

        {(this.props.store.utilities.isConnectedToBackEnd && (
          <form className={s.form} onSubmit={this.handleFormSubmit}>
            <input
              ref={this.referenceUserField}
              tabIndex={1}
              type="text"
              placeholder="Username"
              value={this.username}
              onChange={this.handleFormFieldChange('username')}
            />
            <input
              tabIndex={2}
              type="password"
              placeholder="Password"
              value={this.password}
              onChange={this.handleFormFieldChange('password')}
            />

            <button
              tabIndex={3}
              className="boxButton"
              onClick={this.handleFormSubmit}
            >
              {this.label}
            </button>
          </form>
        )) || (
          <div className={s.offline}>
            <LoadingSpinner active={true} />

            <p>
              <strong>We could not establish connection</strong>

              <span>
                The server might be encountering connectivty problems or the
                issue might be with your network.
                <br />
                <br />
                Please wait while we attempt to reconnect.
              </span>
            </p>
          </div>
        )}

        {this.status === LoginStatus.Success && (
          <Redirect to={{ pathname: this.redirectPath }} />
        )}
      </div>
    );
  }

  private userField: HTMLInputElement = null;
  private referenceUserField = (ref: HTMLInputElement) =>
    (this.userField = ref);

  private handleGoBack = (e: React.MouseEvent<HTMLButtonElement>) => {
    this.props.history.goBack();
  };

  private handleFormFieldChange = (type: 'username' | 'password') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (typeof e !== 'undefined' && typeof e.currentTarget !== 'undefined') {
      this[type] = e.currentTarget.value;
    }
  };

  @action private handleFormSubmit = (
    e: React.FormEvent<HTMLButtonElement | HTMLFormElement>
  ) => {
    e.preventDefault();
    e.stopPropagation();

    this.state = LoginStatus.LoggingIn;

    const endUsername = this.username.trim();
    const endPassword = this.password.trim();

    if (endUsername === '' || endPassword === '') {
      this.status = LoginStatus.AllFieldsRequired;
    } else {
      this.status = LoginStatus.LoggingIn;
      this.handleLogin();
    }
  };

  @action private handleLogin = () => {
    this.props.store.user
      .login(this.username, this.password)
      .then(() => {
        // Indicate success
        this.status = LoginStatus.Success;
      })
      .catch((error) => {
        // Clear form and indicate error
        this.username = '';
        this.password = '';
        this.status = LoginStatus.WrongPasswordOrUsername;

        // Focus on username field
        if (this.userField) {
          this.userField.focus();
        }
      });
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/user/login/Form/LoginForm.tsx